import { SectionName, withTracking } from '../../../observability';
import { createBuilderModule } from '../../../builder';
import { COLOR_KEYS } from '../../../constants';
import { DEFAULT_VALUES } from './fixtures';
import { SplitColorHero } from './SplitColorHero';

export default createBuilderModule(
  withTracking(SplitColorHero, { sectionName: SectionName.SplitColorHero }),
  {
    name: 'SplitColorHero',
    image: 'https://cdn.zappy.app/46111e0323ae9f5a6c5d40dccca0116e.png',
    inputs: [
      {
        name: 'eyebrow',
        type: 'string',
      },
      {
        name: 'headingStaticText',
        type: 'string',
        helperText:
          'Text to be used as a basic heading or the static text to be paired with rotating text',
        showIf: (options) => {
          return (
            !options.get('headingGraphic')?.get('url') &&
            !options.get('headingGraphic')?.get('hiddenText')
          );
        },
      },
      {
        name: 'headingRotatingText',
        type: 'list',
        subFields: [{ name: 'text', type: 'string', required: true }],
        helperText: 'Text to be used in the rotating text animation',
        showIf: (options) => {
          return (
            !!options.get('headingStaticText') &&
            !options.get('headingGraphic')?.get('url') &&
            !options.get('headingGraphic')?.get('hiddenText')
          );
        },
      },
      {
        name: 'headingGraphic',
        type: 'object',
        subFields: [
          {
            name: 'url',
            type: 'cloudinaryImage',
            required: true,
          },
          {
            name: 'hiddenText',
            type: 'string',
            required: true,
            helperText: 'Visually hidden text used of screen readers and SEO',
          },
        ],
        helperText: 'Graphic to be used in place of heading text',
        showIf: (options) => {
          return (
            !options.get('headingStaticText') &&
            !options.get('headingRotatingText')?.length
          );
        },
      },
      {
        name: 'description',
        type: 'richText',
        required: true,
        defaultValue: DEFAULT_VALUES.description,
      },
      {
        name: 'ctaText',
        type: 'string',
        required: true,
        defaultValue: DEFAULT_VALUES.ctaText,
      },
      {
        name: 'ctaUrl',
        type: 'string',
        required: true,
        defaultValue: DEFAULT_VALUES.ctaUrl,
      },
      { name: 'hasGoogleSSO', type: 'boolean' },
      {
        name: 'googleSSOText',
        type: 'string',
        showIf: (options) => options.get('hasGoogleSSO'),
      },
      {
        name: 'secondaryCtaText',
        type: 'string',
        required: false,
        defaultValue: DEFAULT_VALUES.secondaryCtaText,
      },
      {
        name: 'secondaryCtaUrl',
        type: 'string',
        required: false,
        defaultValue: DEFAULT_VALUES.secondaryCtaUrl,
      },
      { name: 'calloutText', type: 'richText', required: false },
      {
        name: 'animationDataUrl',
        friendlyName: 'Animation URL',
        type: 'cloudinaryMediaLibrary',
        helperText: 'URL for either a Lottie JSON or an MP4 video',
        required: false,
      },
      {
        name: 'animationAssetsUrl',
        type: 'string',
        showIf: (options) =>
          !!options.get('animationDataUrl') &&
          options.get('animationDataUrl').toLowerCase().endsWith('.json'),
        helperText:
          'Path to the folder that contains image assets for Lottie animations',
      },
      {
        name: 'animationShouldLoop',
        type: 'boolean',
        required: true,
        defaultValue: DEFAULT_VALUES.animationShouldLoop,
        showIf: (options) => !!options.get('animationDataUrl'),
      },
      {
        name: 'animationSpeed',
        type: 'number',
        showIf: (options) => !!options.get('animationDataUrl'),
      },
      {
        name: 'imageUrl',
        type: 'cloudinaryImage',
        showIf: (options) => !options.get('animationDataUrl'),
      },
      {
        name: 'mediaAltText',
        type: 'string',
        required: true,
      },
      {
        name: 'mediaLinkText',
        type: 'string',
      },
      {
        name: 'mediaLinkUrl',
        type: 'string',
      },
      {
        name: 'backgroundColor',
        type: 'string',
        enum: [
          COLOR_KEYS.moss,
          COLOR_KEYS.neutral800,
          COLOR_KEYS.night,
          COLOR_KEYS.zapier,
          COLOR_KEYS.earth,
        ],
        required: true,
        defaultValue: DEFAULT_VALUES.backgroundColor,
      },
      {
        name: 'isReversedOnMobile',
        friendlyName: 'Reverse sections on mobile',
        type: 'boolean',
      },
    ],
  },
);
